import { useLocation, Link, LinkProps } from "@tanstack/react-router";
import { MenuIcon } from "lucide-react";
import { useMemo } from "react";

import { ContentSearch } from "@/components/ContentSearch";
import { Button, ButtonLink } from "@/components/ui/button";
import {
  DropdownMenu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from "@/components/ui/menu";
import { getUserId } from "@/lib/api";
import { cn } from "@/lib/utils";

function NavLink({
  to,
  href,

  children,
  variant = "teal",
}: {
  to?: LinkProps["to"];
  href?: string;
  children: React.ReactNode;
  variant?: "teal" | "light" | "orange" | "slate";
}) {
  const { pathname } = useLocation();
  const isActive = to && pathname.startsWith(to);
  const Component = to ? Link : "a";

  return (
    <Component
      {...(to ? { to } : { href })}
      className={cn(
        "cursor-pointer-extra",
        "rounded-full px-4 py-2 text-xs",
        variant === "teal" &&
          "text-teal-normal bg-teal-ghost",
        isActive &&
          variant === "teal" &&
          "bg-teal-11 dark:bg-tealdark-10 text-teal-contrast dark:text-tealdark-contrast",
        variant === "light" && [
          "text-slate-7",
          "dark:text-slatedark-7",

          "hover:text-slate-contrast",
          "dark:hovr:text-slatedark-contrast",

          "bg-slatea-10",
          "dark:bg-slatedarka-10",

          "hover:bg-slatea-11",
          "dark:hover:bg-slatedarka-11",

          "focus:bg-slatea-11",
          "dark:focus:bg-slatedarka-11",
        ],
        isActive &&
          variant === "light" && [
            "text-slate-12",
            "dark:text-slatedark-8",

            "hover:text-slate-contrast",
            "dark:hovr:text-slatedark-contrast",

            "bg-slate-9",
            "dark:bg-slatedarka-11",

            "hover:bg-slate-10",

            "focus:bg-slatea-12",
            "dark:focus:bg-slatedarka-12",
          ],
        variant === "orange" && [
          "text-orange-dim hover:text-orange-normal bg-orange-ghost",
          "dark:hover:bg-orangedark-ghost",

          "focus:bg-orange-solid text-orange-contrast dark:text-orangedark-contrast",
        ],
        isActive &&
          variant === "orange" && [
            "text-orange-contrast",
            "dark:text-orangedark-contrast",

            "hover:text-orange-contrast",
            "dark:hovr:text-orangedark-contrast",
          ],
        variant === "slate" && [
          "text-slate-dim hover:text-slate-normal bg-slate-ghost",
          "dark:hover:bg-slatedark-ghost",

          "focus:bg-slate-solid text-slate-contrast dark:text-slatedark-contrast",
        ],
        isActive &&
          variant === "slate" && [
            "text-slate-contrast",
            "dark:text-slatedark-contrast",

            "hover:text-slate-contrast",
            "dark:hovr:text-slatedark-contrast",
          ],
      )}
    >
      {children}
    </Component>
  );
}

function useIsAuthenticated() {
  return useMemo(() => {
    try {
      getUserId();
      return true;
    } catch {
      return false;
    }
  }, []);
}

export function MarketingNavBar({
  onlyNav,
  variant = "teal",
  left,
  showSearch,
  showStartButton,
  className,
}: {
  onlyNav?: boolean;
  variant?: "teal" | "light" | "orange" | "slate";
  left?: React.ReactNode;
  showSearch?: boolean;
  showStartButton?: boolean;
  className?: string;
}) {
  const isAuthed = useIsAuthenticated();

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-8",
        onlyNav && "w-full justify-end",
        className,
      )}
    >
      <div
        className={cn(
          "flex items-center gap-6 flex-1 pl-2",
          onlyNav && "md:hidden",
        )}
      >
        <div className="flex items-center gap-6">
          {left}
          <div className="text-shadow-md">
            <Link
              to="/"
              className={cn(
                "cursor-pointer",
                "sharp-grotesk text-4xl md:text-6xl",
                variant === "light" && "text-slate-2",
              )}
            >
              Graze
            </Link>
          </div>
        </div>
        {showSearch && <ContentSearch variant={variant} />}
      </div>
      <div className="gap-1 items-center hidden md:flex">
        <NavLink to="/marketplace" variant={variant}>
          Marketplace
        </NavLink>
        <NavLink href="https://help.graze.social/en/" variant={variant}>
          Help
        </NavLink>
        <NavLink to="/blog" variant={variant}>
          Blog
        </NavLink>
        <NavLink to="/about" variant={variant}>
          About
        </NavLink>
        {!showStartButton && (
          <NavLink to="/login" variant={variant}>
            Login
          </NavLink>
        )}
        {showStartButton && <ButtonLink to="/login">Start building</ButtonLink>}
      </div>
      <div
        className={cn(
          "md:hidden",
          onlyNav && "ml-auto w-full flex justify-end",
        )}
      >
        <DropdownMenu>
          <MenuTrigger asChild>
            <Button size="icon" variant="ghost">
              <div className="sr-only">Menu</div>
              <MenuIcon />
            </Button>
          </MenuTrigger>
          <MenuContent align="end">
            <MenuItem asChild>
              <Link to="/docs/$slug" params={{ slug: "intro" }}>
                Docs
              </Link>
            </MenuItem>
            <MenuItem asChild>
              <Link to="/blog">Blog</Link>
            </MenuItem>
            <MenuItem asChild>
              <Link to="/marketplace">Marketplace</Link>
            </MenuItem>
            <MenuItem asChild>
              <Link to="/about">About</Link>
            </MenuItem>
            <MenuSeparator />
            {isAuthed ? (
              <MenuItem asChild>
                <Link to="/app">Dashboard</Link>
              </MenuItem>
            ) : showStartButton ? (
              <MenuItem asChild>
                <Link to="/login">Start building</Link>
              </MenuItem>
            ) : (
              <MenuItem asChild>
                <Link to="/login">Login</Link>
              </MenuItem>
            )}
          </MenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
